import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FiPlus, FiEye, FiEdit2, FiTrash2 } from 'react-icons/fi';
import './EmailTemplates.css';
import TemplateForm from '../components/TemplateForm';
import TemplatePreview from '../components/TemplatePreview';

function EmailTemplates({ addToast }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [previewTemplate, setPreviewTemplate] = useState(null);

  const toast = addToast || ((msg, type) => alert(msg));

  useEffect(() => {
    fetchTemplates();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/email-templates');
      setTemplates(response.data.data);
    } catch (error) {
      toast('Failed to fetch templates', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleFormSubmit = async (formData) => {
    try {
      setLoading(true);
      if (editingTemplate) {
        const response = await axios.put(`/api/email-templates/${editingTemplate.id}`, formData);
        setTemplates(templates.map(t => t.id === editingTemplate.id ? response.data.data : t));
        toast('Template updated', 'success');
      } else {
        const response = await axios.post('/api/email-templates', formData);
        setTemplates([response.data.data, ...templates]);
        toast('Template created', 'success');
      }
      setShowForm(false);
      setEditingTemplate(null);
    } catch (error) {
      toast(error.response?.data?.error || 'Failed to save template', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = async (template) => {
    if (!window.confirm(`Delete template "${template.name}"? This cannot be undone.`)) return;
    try {
      setLoading(true);
      await axios.delete(`/api/email-templates/${template.id}`);
      setTemplates(templates.filter(t => t.id !== template.id));
      toast('Template deleted', 'success');
    } catch (error) {
      toast(error.response?.data?.error || 'Failed to delete template', 'error');
    } finally {
      setLoading(false);
    }
  };

  const TYPE_LABELS = {
    initial: 'Initial',
    followup_1: 'Follow-up 1',
    followup_2: 'Follow-up 2'
  };

  return (
    <div className="templates-page">
      <div className="page-header">
        <h1>Email Templates</h1>
        <p>Create and manage templates with dynamic placeholders like {'{{name}}'}, {'{{employer}}'}, {'{{location}}'}</p>
      </div>

      <div className="templates-toolbar">
        <button
          className="btn-form-primary"
          onClick={() => { setEditingTemplate(null); setShowForm(true); }}
          disabled={loading}
        >
          <FiPlus size={15} />
          New Template
        </button>
      </div>

      {loading && !showForm ? (
        <div className="templates-loading">Loading templates...</div>
      ) : templates.length === 0 ? (
        <div className="templates-empty">
          <p>No templates yet.</p>
          <p>Click <strong>New Template</strong> to create your first email template.</p>
        </div>
      ) : (
        <div className="templates-grid">
          {templates.map(template => (
            <div key={template.id} className="template-card">
              <div className="template-card-header">
                <div className="template-card-name">{template.name}</div>
                <span className={`type-badge type-${template.template_type}`}>
                  {TYPE_LABELS[template.template_type] || template.template_type}
                </span>
              </div>

              <div className="template-card-subject">{template.subject}</div>

              <div className="template-card-footer">
                <span className={`active-pill ${template.is_active ? 'active' : 'inactive'}`}>
                  {template.is_active ? 'Active' : 'Inactive'}
                </span>
                <span className="template-date">
                  {new Date(template.created_at).toLocaleDateString()}
                </span>
              </div>

              <div className="template-card-actions">
                <button
                  className="tpl-btn preview"
                  onClick={() => setPreviewTemplate(template)}
                  disabled={loading}
                  title="Preview"
                >
                  <FiEye size={14} /> Preview
                </button>
                <button
                  className="tpl-btn edit"
                  onClick={() => { setEditingTemplate(template); setShowForm(true); }}
                  disabled={loading}
                  title="Edit"
                >
                  <FiEdit2 size={14} /> Edit
                </button>
                <button
                  className="tpl-btn delete"
                  onClick={() => handleDeleteClick(template)}
                  disabled={loading}
                  title="Delete"
                >
                  <FiTrash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showForm && (
        <div className="modal-overlay">
          <div className="modal-content-lg">
            <button className="modal-close-x" onClick={() => { setShowForm(false); setEditingTemplate(null); }}>×</button>
            <TemplateForm
              template={editingTemplate}
              onSubmit={handleFormSubmit}
              onCancel={() => { setShowForm(false); setEditingTemplate(null); }}
              loading={loading}
            />
          </div>
        </div>
      )}

      {previewTemplate && (
        <div className="modal-overlay">
          <div className="modal-content-lg">
            <button className="modal-close-x" onClick={() => setPreviewTemplate(null)}>×</button>
            <TemplatePreview
              template={previewTemplate}
              onClose={() => setPreviewTemplate(null)}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default EmailTemplates;
